import axios, {parseResponseData} from '@/axiosInstance'
import {PaginationParams} from '@/types'
import {NotificationsResponse} from '../types'
import {captureException} from '@sentry/react'
import {z} from 'zod'

export interface HttpGetNotificationsOptions {
    params?: PaginationParams & {
        toRead?: boolean
    }
}

/**
 * Get notifications
 * @param params - Optional parameters
 * @returns Notifications
 */
export const httpGetNotifications = async ({params}: HttpGetNotificationsOptions = {}) => {
    const finalParams = {
        ...params,
        limit: params?.limit || 30,
        page: params?.page || 1
    }

    const response = await axios.get('/notifications', {params: finalParams})
    return parseResponseData({
        responseData: response.data,
        responseShape: NotificationsResponse,
        onZodError: captureException
    })
}

/**
 * Get the count of unread notifications
 * @returns Count of unread notifications
 */
export const httpGetNotificationCount = () => {
    return axios.get('/notifications/count')
}

/**
 * Mark a specific notification as read
 * @param urlParams - URL parameters
 * @returns Notification
 */
export interface HttpMarkNotificationAsReadOptions {
    urlParams: {
        notificationId: string
    }
}

/**
 * Mark a specific notification as read
 * @param urlParams - URL parameters
 * @returns Notification
 */
export const httpMarkNotificationAsRead = ({urlParams}: HttpMarkNotificationAsReadOptions) => {
    return axios.post(`/notifications/${urlParams.notificationId}/mark-as-read`)
}

/**
 * Mark all notifications as read
 * @returns Notification
 */
export const httpMarkAllNotificationsAsRead = () => {
    return axios.post('/notifications/mark-all-as-read')
}

/**
 * Mark notification as read or not
 */
export const MarkNotificationPayload = z.object({
    isRead: z.boolean()
})
export type MarkNotificationPayload = z.infer<typeof MarkNotificationPayload>

export const httpMarkNotification = (notificationId: string, payload: MarkNotificationPayload) =>
    axios.patch(`notifications/${notificationId}`, payload)
